import api from './api';
import { toQueryDateYMD } from '../utils/formatters';

export interface OrdenDevolucionDetalle {
  nroIdformulario: number;
  nroItem: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  nroFormularioRemito?: number;
  nroItemRemito?: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidad?: number;
  precioUnitario?: number;
  descuento?: number;
  subtotal?: number;
  notas?: string;
}

export interface OrdenDevolucion {
  nroFormulario: number;
  nroRemito?: string;
  nroFormularioRemito?: number;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  fechaEmision?: string | Date;
  horaEmision?: string | Date;
  nroTipoFormulario?: string;
  tipoCliente?: string;
  nroCliente?: number;
  nombre?: string;
  nroVendedor?: number;
  nroLista?: string;
  codigoMoneda?: number;
  importeTotal?: number;
  asentado?: string;
  descripcion?: string;
  usuario?: string;
  nroUbicacionOrigen?: number;
  detalles?: OrdenDevolucionDetalle[];
}

export interface OrdenDevolucionFormularioDto {
  descripcion?: string;
  nroSuc?: number;
  nroCliente: number;
  nombre?: string;
  cuit?: string;
  nroLista?: string;
  nroUbicacionOrigen?: number;
  nroFormularioRemito: number;
}

export interface OrdenDevolucionCreateItemDto {
  codigoArticulo: string;
  descripcion?: string;
  cantidad: number;
  precioUnitario: number;
  porcDesc?: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  nroFormularioRemito?: number;
  nroItemRemito?: number;
  notas?: string;
  um?: string;
  lotes?: import('./loteService').LoteAsignacion[];
}

export interface OrdenDevolucionCreateDto {
  formulario: OrdenDevolucionFormularioDto;
  items: OrdenDevolucionCreateItemDto[];
}

export interface OrdenDevolucionSearchParams {
  searchTerm?: string;
  pageNumber: number;
  pageSize: number;
  fechaDesde?: Date | string;
  fechaHasta?: Date | string;
  nroCliente?: string;
  asentado?: string;
  nroSuc?: number;
}

export interface OrdenDevolucionPagedResult {
  items: OrdenDevolucion[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface RemitoPendienteDevolucion {
  nroFormularioRemito: number;
  letra?: string;
  nroSuc: number;
  nroComprobante?: number;
  nroRemito?: string;
  fechaEmision?: string | Date;
  nroCliente?: number;
  nombre?: string;
  nroUbicacionOrigen?: number;
  renglonesConPendiente: number;
  totalPendienteDevolver: number;
}

export interface RemitoPendienteDevolucionPagedResult {
  items: RemitoPendienteDevolucion[];
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  totalPages: number;
}

export interface RemitoPendienteDevolucionDetalle {
  nroFormularioRemito: number;
  nroItemRemito: number;
  nroFormularioMaster?: number;
  nroItemMaster?: number;
  codigoArticulo?: string;
  descripcionArticulo?: string;
  cantidadRemitida: number;
  cantidadDevuelta: number;
  pendienteDevolver: number;
  precioUnitario?: number;
  um?: string;
  notas?: string;
}

const buildSearchParams = (params: OrdenDevolucionSearchParams): Record<string, unknown> => {
  const queryParams: Record<string, unknown> = {
    PageNumber: params.pageNumber,
    PageSize: params.pageSize,
    SearchTerm: params.searchTerm || undefined,
  };
  if (params.fechaDesde) {
    queryParams.FechaDesde = toQueryDateYMD(params.fechaDesde);
  }
  if (params.fechaHasta) {
    queryParams.FechaHasta = toQueryDateYMD(params.fechaHasta);
  }
  if (params.nroCliente) queryParams.NroCliente = params.nroCliente;
  if (params.asentado) queryParams.Asentado = params.asentado;
  if (params.nroSuc != null) queryParams.NroSuc = params.nroSuc;
  return queryParams;
};

const ordenDevolucionService = {
  getOrdenes: async (params: OrdenDevolucionSearchParams): Promise<OrdenDevolucionPagedResult> => {
    const response = await api.get<OrdenDevolucionPagedResult>('/api/OrdenesDevolucion', {
      params: buildSearchParams(params),
    });
    return response.data;
  },

  getOrden: async (id: number): Promise<OrdenDevolucion> => {
    const response = await api.get<OrdenDevolucion>(`/api/OrdenesDevolucion/${id}`);
    return response.data;
  },

  getRemitosPendientes: async (
    params: OrdenDevolucionSearchParams,
  ): Promise<RemitoPendienteDevolucionPagedResult> => {
    const response = await api.get<RemitoPendienteDevolucionPagedResult>(
      '/api/OrdenesDevolucion/pendientes',
      { params: buildSearchParams(params) },
    );
    return response.data;
  },

  getPendientesDeRemito: async (remitoId: number): Promise<RemitoPendienteDevolucionDetalle[]> => {
    const response = await api.get<RemitoPendienteDevolucionDetalle[]>(
      `/api/OrdenesDevolucion/pendientes/${remitoId}`,
    );
    return response.data;
  },

  createOrden: async (payload: OrdenDevolucionCreateDto): Promise<OrdenDevolucion> => {
    const response = await api.post<OrdenDevolucion>('/api/OrdenesDevolucion', payload);
    return response.data;
  },

  anularOrden: async (id: number, motivo?: string): Promise<void> => {
    await api.post(`/api/OrdenesDevolucion/${id}/anular`, { motivo });
  },
};

export default ordenDevolucionService;
